import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { CookieConsentProvider, useCookieConsent } from './contexts/CookieConsentContext';
import Navbar from './components/layout/Navbar';
import ErrorBoundary from './components/ErrorBoundary';
import Home from './pages/Home';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ApplyInviteRedirect from './pages/auth/ApplyInviteRedirect';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import Impersonate from './pages/auth/Impersonate';
import Dashboard from './pages/Dashboard';
import CampProfile from './pages/camps/CampProfile';
import CampCreate from './pages/camps/CampCreate';
import CampEdit from './pages/camps/CampEdit';
import CampDiscovery from './pages/camps/CampDiscovery';
import PublicCampProfile from './pages/camps/PublicCampProfile';
import MemberRoster from './pages/members/MemberRoster';
import MemberProfile from './pages/members/MemberProfile';
import UserProfile from './pages/users/UserProfile';
import AdminDashboard from './pages/admin/AdminDashboard';
import VolunteerShifts from './pages/shifts/VolunteerShifts';
import MyShiftsPage from './pages/shifts/MyShiftsPage';
import Help from './pages/Help';
import FAQAdmin from './pages/help/FAQAdmin';
import HelpPageMockups from './pages/help/HelpPageMockups';
import Principles from './pages/Principles';
import SupportInbox from './components/support/SupportInbox';
import ApplicationManagementTable from './pages/applications/ApplicationManagementTable';
import MyApplications from './pages/applications/MyApplications';
import Contact360View from './pages/contacts/Contact360View';
import RosterManagement from './pages/rosters/RosterManagement';
import MemberProfileEdit from './pages/members/MemberProfileEdit';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import About from './pages/About';
import PublicMemberProfile from './pages/members/PublicMemberProfile';
import CallSlotManagement from './pages/camps/CallSlotManagement';
import TaskManagement from './pages/camps/TaskManagement';
import TaskDetailsPage from './pages/camps/TaskDetailsPage';
import MyTasks from './pages/tasks/MyTasks';
import CampSurveys from './pages/surveys/CampSurveys';
import SurveyRespond from './pages/surveys/SurveyRespond';
import SurveyResponses from './pages/surveys/SurveyResponses';
import SelectRole from './pages/onboarding/SelectRole';
import ShiftsOnlyOnboarding from './pages/onboarding/ShiftsOnlyOnboarding';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CampHelpWidget from './components/help/CampHelpWidget';
import { useLocation } from 'react-router-dom';
import { getDefaultLandingPath } from './utils/authRedirects';

// Conditional Navbar component that hides on onboarding pages
const ConditionalNavbar: React.FC = () => {
  const location = useLocation();
  
  // Hide navbar on onboarding/select-role page
  if (location.pathname === '/onboarding/select-role') {
    return null;
  }
  
  return <Navbar />;
};

// Conditional Main component that adjusts padding based on navbar visibility
interface ConditionalMainProps {
  children: React.ReactNode;
}

const ConditionalMain: React.FC<ConditionalMainProps> = ({ children }) => {
  const location = useLocation();
  
  // No padding top if navbar is hidden
  if (location.pathname === '/onboarding/select-role') {
    return <main>{children}</main>;
  }
  
  return <main className="pt-16">{children}</main>;
};

// Dashboard redirect component for personal accounts and camp accounts
const DashboardRedirect: React.FC = () => {
  const { user } = useAuth();
  
  console.log('🔍 [DashboardRedirect] User account type:', user?.accountType);
  console.log('🔍 [DashboardRedirect] User data:', user);

  const landingPath = getDefaultLandingPath(user);
  if (landingPath !== '/dashboard') {
    console.log('🔍 [DashboardRedirect] Redirecting user to default landing page:', landingPath);
    return <Navigate to={landingPath} replace />;
  }
  
  // Personal accounts go to profile
  if (user?.accountType === 'personal') {
    console.log('🔍 [DashboardRedirect] Redirecting personal account to /user/profile');
    return <Navigate to="/user/profile" replace />;
  }
  
  console.log('🔍 [DashboardRedirect] Showing dashboard for account type:', user?.accountType);
  return <Dashboard />;
};

// Camp Profile redirect component for backward compatibility
const CampProfileRedirect: React.FC = () => {
  const { user } = useAuth();
  
  // Get camp identifier and redirect to new URL format
  const campId = user?.campId?.toString() || '';
  const newPath = campId ? `/camp/${campId}/profile` : '/dashboard';
  
  return <Navigate to={newPath} replace />;
};

// Generic redirect component for camp routes
const CampRouteRedirect: React.FC<{ route: string }> = ({ route }) => {
  const { user } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const queryCampId = params.get('campId');
  
  // Get camp identifier and redirect to new URL format
  const derivedCampId = user?.campId?.toString() || user?.campLeadCampId || user?.eventsLeadCampId || '';
  let newPath = derivedCampId ? `/camp/${derivedCampId}${route}` : '/dashboard';

  // Personal assignees may hit /camp/tasks with explicit campId context from task edit flow.
  if (route === '/tasks' && user?.accountType === 'personal') {
    if (queryCampId) {
      newPath = `/camp/${queryCampId}/tasks`;
    } else {
      newPath = '/tasks';
    }
  }
  
  return <Navigate to={`${newPath}${location.search || ''}`} replace />;
};

const AppShell: React.FC = () => {
  const { canUseAnalytics } = useCookieConsent();

  return (
    <Router>
      <div className="min-h-screen bg-custom-bg">
        <ConditionalNavbar />
        <ConditionalMain>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/apply" element={<ApplyInviteRedirect />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/impersonate" element={<Impersonate />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/about" element={<About />} />
            <Route path="/onboarding/select-role" element={
              <ProtectedRoute>
                <SelectRole />
              </ProtectedRoute>
            } />
            <Route path="/onboarding/shifts-only" element={
              <ProtectedRoute requirePersonalAccount>
                <ShiftsOnlyOnboarding />
              </ProtectedRoute>
            } />
            {/* Legacy dashboard route - redirects based on account type */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardRedirect />
              </ProtectedRoute>
            } />
            
            {/* New identifier-based dashboard route for camp accounts */}
            <Route path="/camp/:campIdentifier/dashboard" element={
              <ProtectedRoute requireCampAccount>
                <Dashboard />
              </ProtectedRoute>
            } />
            
            {/* Legacy route redirects for backward compatibility */}
            <Route path="/camp/profile" element={
              <ProtectedRoute requireCampAccount>
                <CampProfileRedirect />
              </ProtectedRoute>
            } />
            <Route path="/camp/rosters" element={
              <ProtectedRoute requireCampAccount allowEventsLead>
                <CampRouteRedirect route="/roster" />
              </ProtectedRoute>
            } />
            <Route path="/camp/applications" element={
              <ProtectedRoute requireCampAccount>
                <CampRouteRedirect route="/applications" />
              </ProtectedRoute>
            } />
            <Route path="/camp/tasks" element={
              <ProtectedRoute>
                <CampRouteRedirect route="/tasks" />
              </ProtectedRoute>
            } />
            <Route path="/camp/shifts" element={
              <ProtectedRoute requireCampAccount allowEventsLead>
                <CampRouteRedirect route="/events" />
              </ProtectedRoute>
            } />
            <Route path="/camp/surveys" element={
              <ProtectedRoute requireCampAccount allowEventsLead>
                <CampRouteRedirect route="/surveys" />
              </ProtectedRoute>
            } />
            
            {/* New identifier-based routes */}
            <Route path="/camp/:campIdentifier/profile" element={
              <ProtectedRoute requireCampAccount>
                <CampProfile />
              </ProtectedRoute>
            } />
            <Route path="/camp/:campIdentifier/roster" element={
              <ProtectedRoute requireCampAccount allowEventsLead>
                <MemberRoster />
              </ProtectedRoute>
            } />
            <Route path="/camp/:campIdentifier/applications" element={
              <ProtectedRoute requireCampAccount>
                <ApplicationManagementTable />
              </ProtectedRoute>
            } />
            <Route path="/camp/:campIdentifier/tasks" element={
              <ProtectedRoute>
                <TaskManagement />
              </ProtectedRoute>
            } />
            <Route path="/camp/:campIdentifier/events" element={
              <ProtectedRoute requireCampAccount allowEventsLead>
                <VolunteerShifts />
              </ProtectedRoute>
            } />
            <Route path="/camp/:campIdentifier/events/:eventId" element={
              <ProtectedRoute requireCampAccount allowEventsLead>
                <VolunteerShifts />
              </ProtectedRoute>
            } />
            <Route path="/camp/:campIdentifier/surveys" element={
              <ProtectedRoute requireCampAccount allowEventsLead>
                <CampSurveys />
              </ProtectedRoute>
            } />
            <Route path="/camp/:campIdentifier/shifts/:shiftId" element={
              <ProtectedRoute requireCampAccount allowEventsLead>
                <VolunteerShifts />
              </ProtectedRoute>
            } />
            
            {/* Other camp routes */}
            <Route path="/camp/create" element={
              <ProtectedRoute requirePersonalAccount>
                <CampCreate />
              </ProtectedRoute>
            } />
            <Route path="/camp/edit" element={
              <ProtectedRoute requireCampAccount>
                <CampEdit />
              </ProtectedRoute>
            } />
            <Route path="/camp/call-slots" element={
              <ProtectedRoute requireCampAccount>
                <CallSlotManagement />
              </ProtectedRoute>
            } />
            <Route path="/tasks/:taskIdCode" element={
              <ProtectedRoute>
                <TaskDetailsPage />
              </ProtectedRoute>
            } />
            <Route path="/camp/:campId/contacts/member/:memberId" element={
              <ProtectedRoute requireCampAccount>
                <Contact360View />
              </ProtectedRoute>
            } />
            <Route path="/camp/:campId/contacts/:userId" element={
              <ProtectedRoute requireCampAccount>
                <Contact360View />
              </ProtectedRoute>
            } />
            <Route path="/camps" element={<CampDiscovery />} />
            <Route path="/camps/public/:slug" element={<PublicCampProfile />} />
            <Route path="/camps/:slug" element={<PublicCampProfile />} />
            <Route path="/member/profile" element={
              <ProtectedRoute requirePersonalAccount>
                <MemberProfile />
              </ProtectedRoute>
            } />
            <Route path="/member/edit" element={
              <ProtectedRoute requirePersonalAccount>
                <MemberProfileEdit />
              </ProtectedRoute>
            } />
            <Route path="/members/public/:identifier" element={<PublicMemberProfile />} />
            <Route path="/members/:identifier" element={<PublicMemberProfile />} />
            <Route path="/user/profile" element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute requireAdmin>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/help" element={<Help />} />
            <Route path="/camp/help" element={
              <ProtectedRoute requireCampAccount>
                <Help />
              </ProtectedRoute>
            } />
            <Route path="/member/help" element={
              <ProtectedRoute>
                <Help />
              </ProtectedRoute>
            } />
            <Route path="/help/admin" element={
              <ProtectedRoute requireAdmin>
                <FAQAdmin />
              </ProtectedRoute>
            } />
            <Route path="/help/mockups" element={
              <ProtectedRoute>
                <HelpPageMockups />
              </ProtectedRoute>
            } />
            <Route path="/principles" element={<Principles />} />
            <Route path="/support/inbox" element={
              <ProtectedRoute requireCampAccount>
                <SupportInbox />
              </ProtectedRoute>
            } />
            <Route path="/applications/my" element={
              <ProtectedRoute requirePersonalAccount>
                <MyApplications />
              </ProtectedRoute>
            } />
            <Route path="/rosters/manage" element={
              <ProtectedRoute requireCampAccount>
                <RosterManagement />
              </ProtectedRoute>
            } />
            <Route path="/tasks" element={
              <ProtectedRoute requirePersonalAccount>
                <MyTasks />
              </ProtectedRoute>
            } />
            <Route path="/surveys/:surveyId/responses" element={
              <ProtectedRoute requireCampAccount allowEventsLead>
                <SurveyResponses />
              </ProtectedRoute>
            } />
            <Route path="/surveys/:surveyId" element={
              <ProtectedRoute>
                <SurveyRespond />
              </ProtectedRoute>
            } />
            <Route path="/my-shifts" element={
              <ProtectedRoute requirePersonalAccount>
                <MyShiftsPage />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ConditionalMain>
        <CampHelpWidget />
      </div>
      {canUseAnalytics && <Analytics />}
    </Router>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SocketProvider>
          <CookieConsentProvider>
            <AppShell />
          </CookieConsentProvider>
        </SocketProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
